/**
 * The date window control the admin screens share.
 *
 * ## Why one component rather than a `<select>` on each screen
 *
 * Four screens ask the same question and the answer has to mean the same thing
 * on all of them — "7 days" showing eight days of rows on one screen and seven
 * on another is the kind of discrepancy nobody notices until they are comparing
 * two numbers that should agree. The window itself is resolved server-side
 * (`admin/range.ts`); this only names it.
 *
 * ## Why the resolved dates are printed
 *
 * The boundaries are UTC days. That is a defensible default for an
 * operator-facing screen and it is still a choice: at 23:00 in Chicago,
 * "Today" is a window that ends in six hours. The response echoes the window it
 * used, and this prints it, so a reader can see the answer rather than infer
 * it from a label.
 */
import type { JSX } from 'react';

export interface RangeOption { readonly id: string; readonly label: string }

/** Mirrors `RANGE_IDS` in the API. */
export const RANGES: readonly RangeOption[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'all', label: 'All' },
];

export interface ResolvedWindow {
  readonly id: string;
  readonly since: string | null;
  readonly until: string | null;
}

export interface RangePickerProps {
  value: string;
  onChange(id: string): void;
  /** What the server said it used. Printed beside the control. */
  resolved?: ResolvedWindow | null;
  /**
   * Set when the screen cannot answer for the whole window — a snapshot rather
   * than a history. Rendered as a caveat instead of being left implied.
   */
  note?: string;
}

/** `2026-08-18T00:00:00.000Z` -> `18 Aug`. */
function shortUtc(iso: string): string {
  const d = new Date(iso);
  return `${d.getUTCDate()} ${d.toLocaleString('en', { month: 'short', timeZone: 'UTC' })}`;
}

function describe(w: ResolvedWindow): string {
  if (!w.since && !w.until) return 'everything held';
  if (w.since && w.until) return `${shortUtc(w.since)} to ${shortUtc(w.until)} UTC`;
  if (w.since) return `since ${shortUtc(w.since)} UTC`;
  return `up to ${shortUtc(w.until!)} UTC`;
}

export function RangePicker({
  value, onChange, resolved = null, note,
}: RangePickerProps): JSX.Element {
  return (
    <div className="range-picker">
      <div className="tf-group" role="group" aria-label="Date range">
        {RANGES.map((r) => (
          <button
            key={r.id}
            className={`tf compact ${value === r.id ? 'on' : ''}`}
            onClick={() => onChange(r.id)}
            aria-pressed={value === r.id}
          >{r.label}</button>
        ))}
      </div>
      {resolved && <span className="range-window dim">{describe(resolved)}</span>}
      {note && <span className="range-note dim">{note}</span>}
    </div>
  );
}
